/**
 * IA-R3/R7 — One Decision Surface
 * Continue · Hold · Takeover · Needs You
 * (Continue ≠ Approve. Drill Grant stays on the practice desk.)
 * Paint only advertised decision_actions. Never invent Reject or method: POST.
 * Governance / AI Policy must NOT own the live gate.
 */
import { escapeHtml } from "./dom.js";
import { operatorSay } from "../cmc/operator-say.js";

function actionIdsOf(actions) {
  if (!Array.isArray(actions)) return null;
  return new Set(
    actions
      .map((a) => String((a && a.id) || "").trim().toLowerCase())
      .filter(Boolean),
  );
}

function labelOf(actions, id, fallback) {
  if (Array.isArray(actions)) {
    const hit = actions.find((a) => String((a && a.id) || "").toLowerCase() === id);
    if (hit && hit.label) return operatorSay(hit.label, fallback);
  }
  return fallback;
}

function wants(ids, name, aliases, fallback) {
  if (!ids) return fallback;
  if (ids.has(name)) return true;
  return aliases.some((x) => ids.has(x));
}

/**
 * @param {{
 *   incidentId: string,
 *   context?: 'home' | 'workspace' | 'spatial' | 'engine',
 *   actions?: { id?: string, label?: string }[],
 *   waiting?: boolean,
 *   showEscalate?: boolean,
 *   showMoreEvidence?: boolean,
 *   compact?: boolean,
 * }} opts
 */
export function decisionSurfaceActionsHtml(opts = {}) {
  const id = String(opts.incidentId || "").trim();
  if (!id) return "";
  const ctx = String(opts.context || "home");
  const advertised = Array.isArray(opts.actions) ? opts.actions : null;
  const ids = actionIdsOf(advertised);
  const waiting = opts.waiting === true;
  const showContinue = wants(ids, "continue", ["approve"], true);
  const showHold = wants(ids, "hold", [], true);
  const showEsc = wants(
    ids,
    "escalate",
    ["takeover"],
    waiting || opts.showEscalate === true,
  );
  const showEv = wants(
    ids,
    "more_evidence",
    ["more-evidence"],
    waiting || opts.showMoreEvidence === true,
  );
  const showReject = wants(ids, "reject", [], false);
  const sm = " sm";
  const buttons = [];
  if (showContinue) {
    buttons.push(`<button type="button" class="btn${sm} btn-tap" data-ds-action="continue" data-ai-home-allow="${escapeHtml(id)}" title="Continue Team — stay on the loop">${escapeHtml(labelOf(advertised, "continue", "Continue Team"))}</button>`);
  }
  if (showHold) {
    buttons.push(`<button type="button" class="btn${sm} secondary btn-tap" data-ds-action="hold" data-ai-home-hold="${escapeHtml(id)}" title="Hold — you take the wheel">${escapeHtml(labelOf(advertised, "hold", "Hold"))}</button>`);
  }
  if (showReject) {
    buttons.push(`<button type="button" class="btn${sm} ghost btn-tap" data-ds-action="reject" data-ai-home-hold="${escapeHtml(id)}" data-ds-reject="1" title="Reject — stop this auto plan">${escapeHtml(labelOf(advertised, "reject", "Reject"))}</button>`);
  }
  if (showEsc) {
    buttons.push(`<button type="button" class="btn${sm} ghost btn-tap" data-ds-action="escalate" data-ai-home-escalate="${escapeHtml(id)}" title="Takeover — a manager takes the wheel">${escapeHtml(labelOf(advertised, "escalate", "Takeover"))}</button>`);
  }
  if (showEv) {
    buttons.push(`<button type="button" class="btn${sm} ghost btn-tap" data-ds-action="more-evidence" data-ai-home-more-evidence="${escapeHtml(id)}" title="Ask for more proof, then continue">${escapeHtml(labelOf(advertised, "more_evidence", "More evidence"))}</button>`);
  }
  if (!buttons.length) return "";
  return `
    <div class="croc-ds-surface" data-decision-surface="1" data-ds-context="${escapeHtml(ctx)}" data-ds-id="${escapeHtml(id)}" role="group" aria-label="Tonight's next step">
      ${buttons.join("\n      ")}
    </div>`;
}

/** Soft copy when Escalate has no dedicated API yet (IA-R6). */
export function decisionSurfaceEscalateSoftMessage() {
  return "Escalate noted. Hold the plan and open Mission Room. Takeover is not live yet.";
}

/** Soft copy when Request More Evidence has no dedicated API yet (IA-R7). */
export function decisionSurfaceMoreEvidenceSoftMessage() {
  return "More evidence noted. The plan is held. Open the map or photos, then continue when proof is ready.";
}
